import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronDown, CreditCard, LayoutDashboard, LogOut, Menu, UserRound, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth, useLogout } from "@/lib/hooks";
import { isMobile } from "@/lib/config";

const nav = [
  { to: "/blueprint", label: "The Blueprint" },
  { to: "/academy", label: "Academy Portal" },
  { to: "/mentorship", label: "Mentorship" },
  { to: "/deal-analyzer", label: "Deal Analyzer" },
  { to: "/deal-sourcing", label: "Deal Sourcing" },
  { to: "/about", label: "About" },
] as const;

const accountLinks = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, web: false },
  { to: "/billing", label: "Billing", icon: CreditCard, web: true },
  { to: "/profile", label: "Profile", icon: UserRound, web: false },
] as const;

export const joinLabel = isMobile ? "Join the Academy" : "Join for £10/mo";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();
  const links = accountLinks.filter((l) => !(l.web && isMobile));

  const signOut = async () => {
    setOpen(false);
    await logout();
    await navigate({ to: "/" });
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
        <Link to="/" className="flex flex-col leading-none" onClick={() => setOpen(false)}>
          <span className="font-display text-xl tracking-tight">Sir Levantine</span>
          <span className="eyebrow text-[0.6rem] text-muted-foreground">Property Academy</span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {nav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              activeProps={{ className: "text-foreground font-medium" }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent">
                  <span className="flex size-7 items-center justify-center rounded-full bg-[image:var(--gradient-gold)] text-xs font-semibold text-ink">
                    {(user.firstName[0] ?? user.email[0] ?? "?").toUpperCase()}
                  </span>
                  <span className="max-w-32 truncate">{user.firstName || user.email}</span>
                  <ChevronDown className="size-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <p className="text-sm font-medium">{user.fullName}</p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {links.map((l) => (
                  <DropdownMenuItem key={l.to} asChild>
                    <Link to={l.to}>
                      <l.icon className="size-4" />
                      {l.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => void signOut()}>
                  <LogOut className="size-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
                Login
              </Link>
              <Button asChild variant="gold" size="sm">
                <Link to="/signup">{joinLabel}</Link>
              </Button>
            </>
          )}
        </div>

        <button
          className="lg:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>
      </div>

      {open && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-border/60 bg-background px-5 py-4 lg:hidden">
          <div className="flex flex-col gap-4">
            {nav.map((item) => (
              <Link key={item.to} to={item.to} className="text-sm" onClick={() => setOpen(false)}>
                {item.label}
              </Link>
            ))}
            <div className="border-t border-border/60" />
            {user ? (
              <>
                <p className="eyebrow text-muted-foreground">{user.fullName}</p>
                {links.map((l) => (
                  <Link key={l.to} to={l.to} className="text-sm" onClick={() => setOpen(false)}>
                    {l.label}
                  </Link>
                ))}
                <button className="text-left text-sm" onClick={() => void signOut()}>
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm" onClick={() => setOpen(false)}>
                  Login
                </Link>
                <Button asChild variant="gold" size="sm">
                  <Link to="/signup" onClick={() => setOpen(false)}>
                    {joinLabel}
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
